import { Loader } from '@googlemaps/js-api-loader';
import { currentGeoConfig, markMapLoaded, refreshGeoConfig } from '../config';
let loading;
export async function loadGoogle() {
  const config = await refreshGeoConfig();
  if (config.services.map !== 'google') throw new Error('Proveedor de mapas no integrado.');
  if (!config.google.browser_key) throw new Error('Configure la clave web de Google en Configuraciones.');
  if (!loading) {
    const loader = new Loader({ apiKey: config.google.browser_key, version: 'weekly', libraries: ['geometry','places','marker'], language: config.language, region: config.country.toUpperCase() });
    loading = loader.load().then(() => { markMapLoaded(config); return window.google; }).catch(() => { loading = null; throw new Error('No se pudo cargar Google Maps.'); });
  }
  return loading;
}
export function normalizeGooglePlace(place) {
  const parts = place.address_components || [];
  const part = type => parts.find(p => p.types.includes(type))?.long_name || '';
  const country = parts.find(p => p.types.includes('country'))?.short_name?.toLowerCase() || '';
  const location = place.geometry?.location;
  return { provider: 'google', id: place.place_id, address: place.formatted_address || place.description || '',
    short_address: (place.formatted_address || place.description || '').split(',')[0].trim(),
    locality: part('sublocality_level_1') || part('sublocality'), city: part('locality'), country,
    lat: typeof location?.lat === 'function' ? location.lat() : location?.lat,
    lng: typeof location?.lng === 'function' ? location.lng() : location?.lng,
    attribution: { text: 'Google Maps', url: 'https://maps.google.com' } };
}
function googleCall(run) {
  return new Promise((resolve, reject) => run((result, status) => {
    if (status === 'OK') resolve(result);
    else if (status === 'ZERO_RESULTS') resolve(null);
    else reject(new Error(`Google no pudo completar la consulta (${status}).`));
  }));
}
export const googleProvider = {
  async search({ address, sessionToken }) {
    const google = await loadGoogle();
    const result = await googleCall(done => new google.maps.places.AutocompleteService().getPlacePredictions({ input: address, componentRestrictions: { country: currentGeoConfig().country }, sessionToken }, done));
    return { results: (result || []).slice(0,5).map(normalizeGooglePlace) };
  },
  async resolve(candidate, sessionToken) {
    if (Number.isFinite(candidate.lat) && Number.isFinite(candidate.lng)) return candidate;
    const google = await loadGoogle();
    const place = await googleCall(done => new google.maps.places.PlacesService(document.createElement('div')).getDetails({ placeId: candidate.id, sessionToken, fields: ['place_id','formatted_address','address_components','geometry'] }, done));
    if (!place) throw new Error('La dirección seleccionada ya no está disponible.');
    return normalizeGooglePlace(place);
  },
  async reverse({ lat, lng }) {
    const google = await loadGoogle();
    const results = await googleCall(done => new google.maps.Geocoder().geocode({ location: {lat,lng} }, done));
    return { result: results?.[0] ? normalizeGooglePlace(results[0]) : null };
  },
  async route({ origin, destination }) {
    const google = await loadGoogle();
    const result = await googleCall(done => new google.maps.DirectionsService().route({ origin, destination, travelMode: google.maps.TravelMode.DRIVING, provideRouteAlternatives: false, avoidHighways: false, avoidTolls: false, avoidFerries: false }, done));
    if (!result?.routes?.[0]) throw new Error('No se encontró una ruta.');
    const route = result.routes[0]; const leg = route.legs[0];
    return { provider: 'google', geometry: { type: 'LineString', coordinates: route.overview_path.map(p => [p.lng(),p.lat()]) },
      distance_m: leg.distance?.value || 0, duration_s: leg.duration?.value || 0,
      origin, destination, start_address: leg.start_address, end_address: leg.end_address,
      attribution: {text: 'Google Maps', url: 'https://maps.google.com'} };
  },
};
export function navigationLink(lat, lng, type = 'map') {
  const point = `${Number(lat)},${Number(lng)}`;
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng)) || lat == null || lng == null) return '#';
  if (type === 'street_view') return `https://www.google.com/maps?q=&layer=c&cbll=${point}`;
  return `https://www.google.com/maps/search/?api=1&query=${point}`;
}
export async function createSearchSession() {
  const google = await loadGoogle(); return new google.maps.places.AutocompleteSessionToken();
}
export function fitMapBounds(map, points, padding = 50) {
  if (!map || !window.google?.maps || !points.length) return;
  const bounds = new window.google.maps.LatLngBounds(); points.forEach(point => bounds.extend(point)); map.fitBounds(bounds, padding);
}
export const circleSymbol = () => window.google?.maps?.SymbolPath?.CIRCLE || 0;
